import { useMemo, useState, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type {
  AdminOrganizationListItem,
  AdminPropertyListItem,
  PropertyCategory,
  PropertyStatus,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  CalendarIcon,
  CommercialIcon,
  EuroIcon,
  GarageIcon,
  LandIcon,
  OrganizationsIcon,
  PropertiesIcon,
  RulerIcon,
  TagIcon,
} from '@/components/layout/icons';
import { FilterBar, FilterPillGroup } from '@/components/filter-bar';
import { OrganizationFilterRow } from '@/components/organization-filter-row';
import { ListEmptyState, TestBadge, TestWorkspacesPill } from '@/components/test-workspaces';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { readEnumParam, SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';
import { ORGANIZATION_PARAM, useOrganizationFilter } from '@/lib/list-view/use-organization-filter';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';
import { ALL_WORKSPACES_QUERY, includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';

type PropertiesLoaderResult = {
  readonly properties: ReadonlyArray<AdminPropertyListItem>;
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly columnPreference: ColumnPreference;
};

export const propertiesLoaderParams = [] as const;

export async function propertiesLoader(): Promise<PropertiesLoaderResult> {
  const [properties, organizations, columnPreference] = await Promise.all([
    apiClient.adminProperties.list({ query: includeTestWorkspacesQuery() }),
    // A name resolver for the rows, not a list of its own: asked for every
    // workspace so a test-workspace Objekt shown under an active filter still
    // resolves its organization + workspace names (IMM-593.3).
    apiClient.adminOrganizations.list({ query: ALL_WORKSPACES_QUERY }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.properties),
  ]);

  if (!properties.ok) {
    throw new Response('Failed to load properties', { status: properties.status });
  }

  if (!organizations.ok) {
    throw new Response('Failed to load organisations', { status: organizations.status });
  }

  return { properties: properties.body.data, organizations: organizations.body.data, columnPreference };
}

const TYPE_ICON: Record<PropertyCategory, (props: SVGProps<SVGSVGElement>) => ReactElement> = {
  APARTMENT: PropertiesIcon,
  HOUSE: PropertiesIcon,
  COMMERCIAL: CommercialIcon,
  LAND: LandIcon,
  GARAGE_PARKING: GarageIcon,
};

type StatusTone = 'success' | 'neutral' | 'muted';

const STATUS_TONE: Record<PropertyStatus, StatusTone> = {
  ACTIVE: 'success',
  DRAFT: 'neutral',
  IN_ACQUISITION: 'neutral',
  INACTIVE: 'muted',
  ARCHIVED: 'muted',
};

const STATUS_CLASSES: Record<StatusTone, { wrap: string; dot: string }> = {
  success: { wrap: 'bg-success-50 text-success-700', dot: 'bg-success-500' },
  neutral: { wrap: 'bg-neutral-100 text-text-secondary', dot: 'bg-neutral-400' },
  muted: { wrap: 'bg-neutral-50 text-text-tertiary', dot: 'bg-neutral-300' },
};

export const PROPERTY_STATUS_ORDER = [
  'DRAFT',
  'IN_ACQUISITION',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
] as const satisfies ReadonlyArray<PropertyStatus>;

function formatAddress(item: AdminPropertyListItem): string {
  const street = [item.address.street, item.address.houseNumber].filter((p) => p !== null && p !== '').join(' ');
  const city = [item.address.postalCode, item.address.city].filter((p) => p !== '').join(' ');

  return [street, city].filter((p) => p !== '').join(', ');
}

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

function formatEuro(amount: number | null): string {
  if (amount === null) return '—';

  return eurFormatter.format(amount);
}

function formatEuroShort(euros: number): string {
  const millions = euros / 1_000_000;

  if (Math.abs(millions) >= 1) return `€ ${decimalFormatter.format(millions)} M`;

  const thousands = euros / 1_000;

  if (Math.abs(thousands) >= 1) return `€ ${decimalFormatter.format(thousands)} K`;

  return eurFormatter.format(euros);
}

const sqmFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

function formatSqm(value: number | null): string {
  if (value === null) return '—';

  return `${sqmFormatter.format(value)} m²`;
}

// At 1280px the kit's icon heads overflow `main` by 82px (0 at 1440px with labels, 0 at 1280px
// icon-only), so below 1440px the icon heads show only the icon and the head's `title` carries the
// label. Written as a whole literal so Tailwind emits it (BL-043, BL-065; IMM-1074.5 plan).
const LABEL_FROM_1440 = 'hidden min-[1440px]:inline';

function usePropertyColumns(
  orgsById: ReadonlyMap<string, AdminOrganizationListItem>,
): ReadonlyArray<OperatorColumn<AdminPropertyListItem>> {
  const t = useTranslations('pages.admin.properties');
  const format = useLocaleFormat();

  return useMemo(
    () => [
      {
        id: 'object',
        label: t('columns.object'),
        icon: PropertiesIcon,
        headLabelClassName: LABEL_FROM_1440,
        cell: (property) => {
          const TypeIcon = TYPE_ICON[property.category];

          return (
            <div className="flex items-start gap-3">
              <TypeIcon className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary" aria-hidden />
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-text-primary">{property.title}</div>
                <div className="truncate text-base-sm text-text-tertiary">{formatAddress(property)}</div>
              </div>
            </div>
          );
        },
        sortValue: (property) => property.title,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[160px]',
        headLabelClassName: LABEL_FROM_1440,
        cell: (property) => {
          const statusClasses = STATUS_CLASSES[STATUS_TONE[property.status]];

          return (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-base-sm ${statusClasses.wrap}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusClasses.dot}`} aria-hidden />
              {t(`status.${property.status}`)}
            </span>
          );
        },
        sortValue: (property) => t(`status.${property.status}`),
      },
      {
        id: 'price',
        label: t('columns.price'),
        icon: EuroIcon,
        headClassName: 'w-[140px]',
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'font-semibold tabular-nums',
        cell: (property) => formatEuro(property.pricing.askingPrice),
        sortValue: (property) => property.pricing.askingPrice,
      },
      {
        id: 'area',
        label: t('columns.area'),
        icon: RulerIcon,
        headClassName: 'w-[110px]',
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'tabular-nums',
        cell: (property) => formatSqm(property.areas.livingArea),
        sortValue: (property) => property.areas.livingArea,
      },
      {
        id: 'tenancy',
        label: t('columns.tenancy'),
        icon: OrganizationsIcon,
        headClassName: 'w-[220px]',
        headLabelClassName: LABEL_FROM_1440,
        cell: (property) => {
          const org = orgsById.get(property.organizationId);

          return (
            <div className="min-w-0">
              <div className="truncate text-base font-semibold text-text-primary">{org?.name ?? '—'}</div>
              <div className="flex items-center gap-2 text-base-sm text-text-tertiary">
                <span className="truncate">{org?.workspaceName ?? '—'}</span>
                <TestBadge isTest={property.isTest} />
              </div>
            </div>
          );
        },
        sortValue: (property) => orgsById.get(property.organizationId)?.name ?? null,
      },
      // `created-at`, not `last-activity`: the value below is property.createdAt,
      // and AdminPropertyListItemSchema carries no activity timestamp to head a
      // „Letzte Aktivität" column with. IMM-532 / BL-027.
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: CalendarIcon,
        headClassName: 'w-[160px]',
        headLabelClassName: LABEL_FROM_1440,
        cellClassName: 'text-text-tertiary',
        cell: (property) => format.ago(property.createdAt),
        sortValue: (property) => Date.parse(property.createdAt),
      },
    ],
    [t, orgsById, format],
  );
}

export function PropertiesPage() {
  const t = useTranslations('pages.admin.properties');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { properties, organizations, columnPreference } = useLoaderData() as PropertiesLoaderResult;
  const navigate = useNavigate();
  const orgsById = useMemo(() => new Map(organizations.map((org) => [org.id, org])), [organizations]);
  const [, setHoverId] = useState<string | null>(null);

  const { params, setParams } = useListParams();

  // workspaceId → name (a Standort carries both; many Standorte may share a workspace).
  const workspaceNames = useMemo(() => {
    const map = new Map<string, string>();

    for (const org of organizations) {
      if (!map.has(org.workspaceId)) map.set(org.workspaceId, org.workspaceName);
    }

    return map;
  }, [organizations]);
  const workspaceRows = useMemo(
    () =>
      properties.map((p) => ({ workspaceId: p.workspaceId, workspaceName: workspaceNames.get(p.workspaceId) ?? null })),
    [properties, workspaceNames],
  );
  const workspace = useWorkspaceFilter(workspaceRows, { clearWith: [ORGANIZATION_PARAM] });
  const organization = useOrganizationFilter(workspace.workspaceId, organizations);
  const status = readEnumParam(params, 'status', PROPERTY_STATUS_ORDER);
  const search = params.get(SEARCH_PARAM) ?? '';

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return properties.filter((property) => {
      if (status !== null && property.status !== status) return false;

      if (workspace.workspaceId !== null && property.workspaceId !== workspace.workspaceId) return false;

      if (organization.organizationId !== null && property.organizationId !== organization.organizationId) return false;

      if (needle !== '' && !`${property.title} ${formatAddress(property)}`.toLowerCase().includes(needle)) return false;

      return true;
    });
  }, [properties, status, workspace.workspaceId, organization.organizationId, search]);

  const catalogue = usePropertyColumns(orgsById);
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.properties,
    catalogue,
    alwaysVisibleId: 'object',
    loadedPreference: columnPreference,
  });

  const totals = useMemo(() => {
    const priced = filtered.map((p) => p.pricing.askingPrice).filter((c): c is number => c !== null);
    const areas = filtered.map((p) => p.areas.livingArea).filter((a): a is number => a !== null);

    return {
      sumEuros: priced.reduce((acc, v) => acc + v, 0),
      pricedCount: priced.length,
      avgArea: areas.length > 0 ? areas.reduce((acc, v) => acc + v, 0) / areas.length : null,
    };
  }, [filtered]);

  const serverEmpty = properties.length === 0;

  const totalsCell: Readonly<Record<string, ReactNode>> = {
    object: t('totals.shown-of-total', { shown: String(filtered.length), total: String(properties.length) }),
    status: t('totals.empty'),
    price:
      totals.pricedCount > 0 ? t('totals.price-sum', { amount: formatEuroShort(totals.sumEuros) }) : t('totals.empty'),
    area:
      totals.avgArea !== null
        ? t('totals.area-avg', { amount: formatSqm(Math.round(totals.avgArea)) })
        : t('totals.empty'),
    tenancy: t('totals.empty'),
  };

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={PropertiesIcon} />} />
      <FilterBar
        fixedPills={
          <>
            <FilterPillGroup
              label={tFilters('status')}
              allLabel={tFilters('all')}
              icon={TagIcon}
              options={PROPERTY_STATUS_ORDER.map((value) => ({ value, label: t(`status.${value}`) }))}
              value={status}
              onChange={(next) => setParams({ status: next })}
            />
            <TestWorkspacesPill />
          </>
        }
        pills={workspace.pills}
        {...(workspace.pills !== null ? { overflow: workspace.overflow } : {})}
        searchValue={search}
        searchPlaceholder={t('filters.search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {workspace.workspaceId !== null && <OrganizationFilterRow filter={organization} />}
      {filtered.length === 0 ? (
        <ListEmptyState title={serverEmpty ? t('empty') : t('empty-filtered')} serverEmpty={serverEmpty} />
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((property) => (
              <TableRow
                key={property.id}
                interactive
                onClick={() => navigate(`/workspaces/properties/${property.id}`)}
                onMouseEnter={() => setHoverId(property.id)}
                onMouseLeave={() => setHoverId(null)}
              >
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(property)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          <tfoot className="bg-neutral-50 text-text-tertiary">
            <tr className="h-table-row align-middle">
              {table.columns.visibleColumns.map((column) => (
                <td
                  key={column.id}
                  className={cn(
                    'border-t border-border px-4 text-base-sm',
                    (column.id === 'price' || column.id === 'area') && 'tabular-nums',
                    table.columns.bodyCellClassName(column.id),
                  )}
                >
                  {totalsCell[column.id]}
                </td>
              ))}
            </tr>
          </tfoot>
        </Table>
      )}
    </>
  );
}
